import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Mail, Lock } from "lucide-react";
import { useAuth } from "../AuthContext";
import { GoogleAuthButton } from "../components/GoogleAuthButton";

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="cc cc-bg min-h-screen flex items-center justify-center px-4 py-10">
    <div className="pointer-events-none absolute top-0 left-1/4 h-72 w-72 rounded-full bg-blue-600/10 blur-[100px]" />
    <div className="pointer-events-none absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-purple-600/10 blur-[100px]" />
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
      className="relative w-full max-w-md"
    >
      <Link to="/credit" className="flex items-center justify-center gap-2.5 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl cc-gradient-br cc-glow-blue">
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="cc-heading text-lg font-600 text-white">ArcaCentum <span className="cc-gradient-text">Credit</span></div>
      </Link>
      <div className="cc-card rounded-2xl p-7 sm:p-8">
        <h1 className="cc-heading text-2xl font-600 text-white text-center">{title}</h1>
        <p className="text-slate-400 text-sm text-center mt-1.5">{subtitle}</p>
        <div className="mt-7">{children}</div>
      </div>
      <p className="text-center text-sm text-slate-400 mt-6">{footer}</p>
    </motion.div>
  </div>
);

const Field = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
    <input {...props} className="cc-input w-full rounded-xl pl-10 pr-4 py-3 text-sm" />
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate("/credit/app");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell
      title="Log in to your account"
      subtitle="Continue building better credit"
      footer={<>Don't have an account? <Link to="/credit/signup" className="text-blue-300 hover:text-blue-200">Sign up free</Link></>}
    >
      <GoogleAuthButton mode="login" testid="google-login-btn" />
      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-white/[0.06]" />
        <span className="text-xs text-slate-500">or</span>
        <div className="h-px flex-1 bg-white/[0.06]" />
      </div>
      <form onSubmit={submit} className="space-y-3.5">
        <Field icon={Mail} type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        <Field icon={Lock} type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        <div className="flex justify-end -mt-1">
          <Link to="/credit/forgot-password" className="text-xs text-slate-400 hover:text-blue-300" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit" className="cc-btn-primary flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-500 disabled:opacity-60">
          {loading ? "Logging in…" : <>Log in <ArrowRight size={16} /></>}
        </button>
      </form>
    </AuthShell>
  );
}
